"use client";

import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { legacyComponentCls } from "@faire/web/slate/utils/tracking";
import ClickOutside from "@faire/web/ui/ClickOutside";
import { Popover, PopoverContent } from "@faire/web/ui/Popover";
import * as React from "react";

import { ClearButton } from "./__internal__/ClearButton";
import { DropdownContent } from "./__internal__/DropdownContent";
import { DropdownInput } from "./__internal__/DropdownInput";
import { MobileDropdown } from "./__internal__/MobileDropdown";
import { useDropdownInteractions } from "./__internal__/useDropdownInteractions";
import { useDropdownState } from "./__internal__/useDropdownState";
import { isCustomOption } from "./__internal__/utils";
import { ClickOutsideArea, DROPDOWN_LIST_HEIGHT } from "./constants";
import { Wrapper } from "./styles";
import { IProps } from "./types";

interface IPortalOptions {
  /** Prevents the dropdown from overflowing its container */
  disableOverflow?: boolean;
  /** Prevents the popover from opening upwards when there is not enough space below the input */
  disableFlip?: boolean;
  /** The z-index of the dropdown */
  zIndex?: number;
  /** If true, the dropdown will have a z-index large enough to escape the modal */
  isModal?: boolean;
}

interface IPortalDropdownProps<T extends string = string>
  extends IProps<T>,
    IPortalOptions {}

export const PortalDropdown = <T extends string = string>(
  props: IPortalDropdownProps<T>
) => {
  const {
    options,
    filter,
    onSelect,
    value,
    containingElement,
    disableInput,
    className,
    placeholder,
    label,
    error,
    helper,
    createNewOption,
    disabled,
    autoComplete = "off",
    id,
    inputRef,
    borderless,
    customOption,
    "aria-label": ariaLabel,
    "data-test-id": dataTestId,
    "data-hj-allow": dataHjAllow,
    useTabletAndAboveDropdownStyle,
    onDropdownOpen,
    onDropdownClose,
    onClear,
    customOptionMaxLength,
    disableOverflow,
    disableFlip,
    zIndex,
    isModal,
  } = props;

  const {
    dropdownId,
    targetRef,
    refs,
    open,
    setOpen,
    searchValue,
    setSearchValue,
    highlightIndex,
    setHighlightIndex,
    isCustomizingOption,
    setIsCustomizingOption,
    filteredOptions,
    ariaProps,
    showErrorState,
    showErrorMessage,
    errorMessage,
    showMobileDropdown,
  } = useDropdownState({
    id,
    inputRef,
    filter,
    options,
    customOption,
    createNewOption,
    ariaLabel,
    label,
    error,
    useTabletAndAboveDropdownStyle,
  });

  const {
    closeDropdown,
    handleClickOutside,
    handleInputClick,
    handleInputKeyDown,
    handleKeyDown,
    handleValueSelect,
    handleOptionSelect,
    handleInputSearch,
    handleCustomOptionEdit,
    resetSearchValue,
  } = useDropdownInteractions({
    createNewOption,
    customOption,
    filteredOptions,
    highlightIndex,
    isCustomizingOption,
    onDropdownClose,
    onDropdownOpen,
    onSelect,
    open,
    options,
    refs,
    searchValue,
    setHighlightIndex,
    setIsCustomizingOption,
    setOpen,
    setSearchValue,
    value,
    targetRef,
  });

  const isSSR = useIsSSR();
  const isPortal = !isSSR && !disableOverflow;

  const inputBoxRef = React.useRef<HTMLDivElement | null>(null);

  if (showMobileDropdown) {
    return (
      <MobileDropdown
        borderless={borderless}
        className={legacyComponentCls(className)}
        dataTestId={dataTestId}
        disabled={disabled}
        errorMessage={errorMessage}
        options={options}
        helper={helper}
        id={dropdownId}
        label={label}
        onOpen={onDropdownOpen}
        onSelect={handleValueSelect}
        placeholder={placeholder}
        showErrorMessage={showErrorMessage}
        showErrorState={showErrorState}
        value={value}
      />
    );
  }

  return (
    <Wrapper
      data-test-id={`dropdown-${label || "default"}`}
      className={legacyComponentCls(className)}
      $disabled={disabled}
      $disableInput={disableInput}
      {...ariaProps}
    >
      <ClickOutside
        onClickOutside={() => handleClickOutside(ClickOutsideArea.INPUT)}
      >
        <Popover
          open={open}
          containingElement={containingElement}
          height={DROPDOWN_LIST_HEIGHT}
          disableOverflow={disableOverflow}
          onClickOutside={() => handleClickOutside(ClickOutsideArea.MENU)}
          anchorRef={inputBoxRef.current ?? undefined}
          zIndex={zIndex}
          onKeyDown={handleKeyDown}
          isModal={isModal}
          disableFlip={disableFlip}
        >
          <DropdownInput
            id={dropdownId}
            dataTestId={dataTestId}
            disabled={disabled}
            label={label}
            helper={helper}
            error={error}
            placeholder={placeholder}
            disableSearch={disableInput}
            onKeyDown={handleInputKeyDown}
            targetRef={targetRef}
            borderless={borderless}
            autoComplete={autoComplete}
            ariaLabel={ariaLabel}
            value={value}
            dataHjAllow={dataHjAllow}
            onClear={onClear}
            maxLength={customOptionMaxLength}
            onClick={handleInputClick}
            options={filteredOptions}
            searchValue={searchValue}
            onSearch={handleInputSearch}
            inputBoxRef={inputBoxRef}
          />
          <PopoverContent>
            <DropdownContent
              createNewOption={createNewOption}
              searchValue={searchValue}
              closeDropdown={closeDropdown}
              resetSearchValue={resetSearchValue}
              showErrorMessage={showErrorMessage}
              helper={helper}
              open={open}
              filteredOptions={filteredOptions}
              isCustomOption={isCustomOption}
              onSelect={handleOptionSelect}
              highlightIndex={highlightIndex}
              onHighlight={setHighlightIndex}
              refs={refs}
              targetRef={targetRef}
              containingElement={containingElement}
              options={options}
              onCustomOptionEdit={handleCustomOptionEdit}
              isPortal={isPortal}
            />
          </PopoverContent>
        </Popover>
      </ClickOutside>
      <ClearButton value={value} onClear={onClear} helper={helper} />
    </Wrapper>
  );
};

PortalDropdown.displayName = "PortalDropdown";
